/**
 * The call rows, wherever they are read.
 *
 * Lifted out of CallsPage unchanged so the dashboard's Recall list can be the
 * same table over the same endpoint — `GET /calls`, with `recall_only` set —
 * rather than a second table that looks similar over a second source. That is
 * what makes correcting a row from either screen the same edit to the same
 * record, which was the whole point of the exercise.
 *
 * It owns no data and no mutations. Rows come in, intents go out: the page
 * that mounts it decides what Track, Follow up, Edit and Delete actually do,
 * and keeps its own dialogs. A table that fetched for itself would have two
 * callers disagreeing about which filters were in force.
 *
 * The columns vary because the two surfaces have different room and different
 * questions. The report shows when the call happened; Recall shows when the
 * promise about it fell due, which is the number that makes the row urgent.
 */
import { Pencil, Phone, PhoneCall, Trash2, UserPlus } from 'lucide-react';
import { formatPhone, telHref } from '../lib/phone';
import { formatFilterDate } from '../lib/filterDate';
import { titleCaseName } from '../lib/titleCase';
import { usePositiveOutcomes } from '../lib/usePositiveOutcomes';
import type { Call, CallOutcome } from '../types';

const NEGATIVE = new Set<CallOutcome>([
  'not_interested',
  'invalid_number',
  'not_suitable',
  'wrong_profile',
  'already_working',
  'not_looking',
]);

export function OutcomeBadge({
  outcome,
  positive,
}: {
  outcome: CallOutcome;
  positive: Set<CallOutcome>;
}) {
  const tone = positive.has(outcome)
    ? 'bg-emerald-100 text-emerald-700'
    : NEGATIVE.has(outcome)
      ? 'bg-gray-100 text-gray-600'
      : 'bg-amber-100 text-amber-700';

  return (
    <span className={`badge ${tone}`}>{outcome.replace(/_/g, ' ')}</span>
  );
}

/** How overdue a chase is, in the words somebody would use out loud. */
function overdueBy(dueAt: string): string {
  const days = Math.floor(
    (Date.now() - new Date(dueAt).getTime()) / 86_400_000,
  );
  if (days <= 0) return 'today';
  if (days === 1) return '1 day';
  return `${days} days`;
}

export interface CallsTableProps {
  rows: Call[];
  /** Whose call each row is. Off where every row belongs to the reader. */
  showsRecruiter?: boolean;
  /**
   * Which date the first column carries.
   *
   * `call` is when it happened, which is what the report is ordered by.
   * `due` is when the chase about it fell due — the Recall question, and the
   * reason a row is on that list at all.
   */
  dateColumn?: 'call' | 'due';
  /** Off on the dashboard, where the column is the first thing to lose. */
  showPosition?: boolean;
  /** Disables the two actions that fire a mutation the parent owns. */
  busy?: boolean;
  emptyMessage?: string;
  onFollowUp: (row: Call) => void;
  onEdit: (row: Call) => void;
  /**
   * Omit to withhold the action entirely.
   *
   * Presence is the permission, rather than a separate boolean beside it. The
   * dashboard passes neither: Track belongs to the recruitment flow on the
   * report, and Delete is the owner's junk removal, which CallsPage supplies
   * only for a company admin. One thing to get wrong instead of two.
   */
  onTrack?: (row: Call) => void;
  onDelete?: (row: Call) => void;
}

export default function CallsTable({
  rows,
  showsRecruiter = false,
  dateColumn = 'call',
  showPosition = true,
  busy = false,
  emptyMessage = 'No calls logged yet.',
  onTrack,
  onFollowUp,
  onEdit,
  onDelete,
}: CallsTableProps) {
  const positive = usePositiveOutcomes();
  const columnCount =
    5 + (showsRecruiter ? 1 : 0) + (showPosition ? 1 : 0);

  return (
    <div className="overflow-x-auto rounded-xl bg-white ring-1 ring-gray-200">
      <table className="w-full text-sm">
        <thead className="border-b border-gray-200 bg-gray-50 text-left text-xs  text-gray-500">
          <tr>
            <th className="px-4 py-2.5 font-medium">
              {dateColumn === 'due' ? 'Due' : 'Date'}
            </th>
            {showsRecruiter && (
              <th className="px-4 py-2.5 font-medium">Recruiter</th>
            )}
            <th className="w-64 min-w-64 px-4 py-2.5 font-medium">Candidate</th>
            <th className="px-4 py-2.5 font-medium">Mobile</th>
            <th className="px-4 py-2.5 font-medium">Client</th>
            {showPosition && (
              <th className="px-4 py-2.5 font-medium">Position</th>
            )}
            {/* "Remarks", matching the filter above it and the column the
                tracker sheets carry -- what the business calls this is the
                recruiter's note, and the outcome is what the importer
                normalises that note into.

                Note the log-a-call form still says "Outcome" for this field,
                because a free-text Remarks box sits directly beside it there.
                Two adjacent controls with the same label would be worse than
                the inconsistency. */}
            <th className="px-4 py-2.5 font-medium">Remarks</th>
            <th className="px-4 py-2.5 font-medium">
              <span className="sr-only">Track</span>
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((row) => (
            <tr key={row.id} className="hover:bg-gray-50">
              <td className="whitespace-nowrap px-4 py-2.5 text-gray-500">
                {dateColumn === 'due' ? (
                  /* The promise, not the call. A row is on Recall because
                     this date has passed, so the overdue span is the
                     number worth reading first and the date is the detail
                     behind it. */
                  row.recallDueAt ? (
                    <>
                      <span className="font-medium text-red-600">
                        {overdueBy(row.recallDueAt)}
                      </span>
                      <span className="ml-1.5 text-xs text-gray-400">
                        {formatFilterDate(row.recallDueAt.slice(0, 10))}
                      </span>
                    </>
                  ) : (
                    <span className="text-gray-300">—</span>
                  )
                ) : (
                  <>
                    {/* The way the sheets write it. The column carried the
                        stored ISO value, which is the one spelling nobody
                        here uses. */}
                    {formatFilterDate(row.callDate)}
                    {/* Only when it is known. A null is "not recorded" — it is
                        null for every imported call — and rendering it as
                        00:00 would state a time nobody supplied. */}
                    {row.calledAt && (
                      <span className="ml-1.5 text-xs text-gray-400">
                        {new Date(row.calledAt).toLocaleTimeString('en-IN', {
                          hour: 'numeric',
                          minute: '2-digit',
                        })}
                      </span>
                    )}
                  </>
                )}
              </td>
              {showsRecruiter && (
                <td className="whitespace-nowrap px-4 py-2.5 text-gray-600">
                  {titleCaseName(row.recruiterName) ?? (
                    <span className="text-gray-300">—</span>
                  )}
                </td>
              )}
              <td className="w-64 min-w-64 px-4 py-2.5 font-medium text-gray-900">
                {titleCaseName(row.candidateName) ?? '—'}
              </td>
              <td className="whitespace-nowrap px-4 py-2.5">
                {row.candidatePhone ? (
                  <a
                    href={`tel:${telHref(row.candidatePhone)}`}
                    className="inline-flex items-center gap-1 text-indigo-600 hover:underline"
                  >
                    <Phone className="h-3.5 w-3.5" />
                    {formatPhone(row.candidatePhone)}
                  </a>
                ) : (
                  /* A call whose candidate has no number on file. Worth
                     seeing: it is the one that cannot be followed up. */
                  <span className="text-gray-400">—</span>
                )}
              </td>
              <td className="px-4 py-2.5 text-gray-600">{row.clientName ?? '—'}</td>
              {showPosition && (
                <td className="px-4 py-2.5 text-gray-600">{row.position ?? '—'}</td>
              )}
              <td className="px-4 py-2.5">
                <OutcomeBadge outcome={row.outcome} positive={positive} />
              </td>
              <td className="whitespace-nowrap px-4 py-2.5 text-right">
                {/* Offered on the outcomes that mean the candidate is in
                    play. A call with a client goes straight through; one
                    without asks which client, because that is the only thing
                    a submission needs that a call may not have. */}
                {onTrack && positive.has(row.outcome) && (
                  <button
                    type="button"
                    className="btn-ghost text-xs"
                    disabled={busy}
                    onClick={() => onTrack(row)}
                    title="Submit this candidate to the client, so they appear on the Tracker"
                  >
                    <UserPlus className="h-3.5 w-3.5" />
                    Track
                  </button>
                )}
                {/* Every row on this report is somebody's most recent call,
                    left open. Ringing them again is the next thing that
                    happens to all of them, so this is offered on the row
                    rather than hidden behind the log-a-call form at the top of
                    the page.

                    Gated on a candidate: a call with none has nobody to follow
                    up with. */}
                {row.candidateId && (
                  <button
                    type="button"
                    className="btn-ghost ml-1 text-xs"
                    disabled={busy}
                    onClick={() => onFollowUp(row)}
                    title="Log a second call to this candidate, keeping this one"
                  >
                    <PhoneCall className="h-3.5 w-3.5" />
                    Follow up
                  </button>
                )}
                {/* Correcting what was logged. Offered on every row: the
                    fields that carry a consequence gate themselves, and a
                    button that appears on some rows and not others teaches
                    nobody which is which. */}
                <button
                  type="button"
                  className="btn-ghost ml-1 text-xs"
                  onClick={() => onEdit(row)}
                  title="Correct this call"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </button>
                {/* Junk removal. Refuses while a follow-up points at the call,
                    so a promise never loses the call that produced it. */}
                {onDelete && (
                  <button
                    type="button"
                    className="btn-ghost ml-1 text-xs text-red-600"
                    onClick={() => onDelete(row)}
                    title="Delete this call, if nothing references it"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr>
              <td
                colSpan={columnCount}
                className="px-4 py-8 text-center text-gray-400"
              >
                {emptyMessage}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
